import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchHiddenCoins,
  type HiddenCoinsListResult,
  type HideCoinResult,
  hideCoin,
  type UnhideCoinResult,
  unhideCoin,
} from "./hidden-coins-api";
import { applyHide, applyUnhide, type HiddenCoinsState } from "./hidden-match";

export type { HiddenCoinsState };

/** Исход действия со скрытой монетой для вызывающей стороны. */
export type HiddenCoinOutcome = "ok" | "invalid" | "unavailable";

interface UseHiddenCoinsOptions {
  onSessionInvalid: () => void;
}

export interface UseHiddenCoinsResult {
  state: HiddenCoinsState;
  /** Скрыть монету целиком (§75). Ответ ложится в список без перечитывания. */
  hide: (base: string) => Promise<HiddenCoinOutcome>;
  /** Вернуть монету из листа «Скрытые монеты». */
  unhide: (base: string) => Promise<HiddenCoinOutcome>;
  /**
   * Монеты, чьё собственное действие ещё не завершилось: повторное нажатие до
   * ответа игнорируется (тот же приём, что `pendingBases` избранного).
   */
  pendingBases: ReadonlySet<string>;
  /** Перечитать список — «Повторить» листа, когда чтение не удалось. */
  reload: () => Promise<void>;
}

/**
 * Состояние списка после чтения. Неудача поверх уже прочитанного списка его не
 * гасит: данные остаются последними известными. Протухшая сессия на первом
 * чтении даёт `unavailable`, а не вечный `loading` — тот же довод, что у
 * `useDisplayMinimums`.
 */
export function hiddenCoinsStateFromList(
  result: HiddenCoinsListResult,
  previous: HiddenCoinsState,
): HiddenCoinsState {
  if (result.status === "ok") {
    return { status: "ready", coins: result.coins };
  }
  return previous.status === "ready" ? previous : { status: "unavailable" };
}

/**
 * Занятость монеты держится ровно этим множеством: второй вызов той же монеты,
 * пока первый в пути, возвращает `null` — второй запрос не уходит. Проверка и
 * пометка идут синхронно, до первого `await`.
 */
export function guardedHiddenCoinAction<T>(
  pending: Set<string>,
  base: string,
  send: () => Promise<T>,
): Promise<T> | null {
  if (pending.has(base)) {
    return null;
  }
  pending.add(base);
  return send().finally(() => pending.delete(base));
}

interface RunDeps<R> {
  send: () => Promise<R>;
  update: (change: (previous: HiddenCoinsState) => HiddenCoinsState) => void;
}

interface RunHideDeps extends RunDeps<HideCoinResult> {
  /** Список уже прочитан: ответ можно класть в него без перечитывания. */
  isReady: () => boolean;
  reload: () => Promise<void>;
}

/**
 * Скрытие: запрос и, на успехе, правка списка по ответу. Не прочитанный список
 * (`loading`/`unavailable`) ответ принять не может, а монета на сервере уже
 * скрыта: список перечитывается, иначе она молча оставалась бы на экране, а
 * при гонке с первым `GET` пришедший список мог бы её не содержать (Д6).
 */
export async function runHideCoin(deps: RunHideDeps): Promise<HiddenCoinOutcome | "unauthorized"> {
  const result = await deps.send();
  if (result.status === "ok") {
    if (deps.isReady()) {
      deps.update((previous) => applyHide(previous, result.coin));
    } else {
      await deps.reload();
    }
    return "ok";
  }
  return result.status;
}

/** Возврат: запрос и, на успехе, правка списка. */
export async function runUnhideCoin(
  base: string,
  deps: RunDeps<UnhideCoinResult>,
): Promise<HiddenCoinOutcome | "unauthorized"> {
  const result = await deps.send();
  if (result.status === "ok") {
    deps.update((previous) => applyUnhide(previous, base));
    return "ok";
  }
  return result.status;
}

/**
 * Скрытые монеты §6.10 общим списком пользователя: загрузка при входе, правка
 * по ответу собственного действия, без опроса. Один экземпляр на каркас
 * (`app-shell.tsx`): лист меню пользователя возвращает монеты, экран плотностей
 * читает тот же список и отбирает строки сразу, без перечитывания.
 */
export function useHiddenCoins({ onSessionInvalid }: UseHiddenCoinsOptions): UseHiddenCoinsResult {
  const [state, setState] = useState<HiddenCoinsState>({ status: "loading" });
  const [pendingBases, setPendingBases] = useState<ReadonlySet<string>>(new Set());
  const pendingRef = useRef<Set<string>>(new Set());
  // Ответ скрытия читает актуальное состояние, а не замкнутое на момент нажатия.
  const stateRef = useRef(state);
  stateRef.current = state;

  const reload = useCallback(async (): Promise<void> => {
    setState((previous) => (previous.status === "ready" ? previous : { status: "loading" }));
    const result = await fetchHiddenCoins();
    if (result.status === "unauthorized") {
      setState((previous) => hiddenCoinsStateFromList(result, previous));
      onSessionInvalid();
      return;
    }
    setState((previous) => hiddenCoinsStateFromList(result, previous));
  }, [onSessionInvalid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const act = useCallback(
    async (
      base: string,
      run: () => Promise<HiddenCoinOutcome | "unauthorized">,
    ): Promise<HiddenCoinOutcome> => {
      const outcome = guardedHiddenCoinAction(pendingRef.current, base, run);
      if (outcome === null) {
        // Запрос по этой монете уже в пути; вызывающая сторона это проверяет
        // сама, здесь крайний случай той же гонки.
        return "unavailable";
      }
      setPendingBases(new Set(pendingRef.current));
      const result = await outcome;
      setPendingBases(new Set(pendingRef.current));
      if (result === "unauthorized") {
        onSessionInvalid();
        return "unavailable";
      }
      return result;
    },
    [onSessionInvalid],
  );

  const hide = useCallback(
    (base: string) =>
      act(base, () =>
        runHideCoin({
          send: () => hideCoin(base),
          update: setState,
          isReady: () => stateRef.current.status === "ready",
          reload,
        }),
      ),
    [act, reload],
  );

  const unhide = useCallback(
    (base: string) =>
      act(base, () => runUnhideCoin(base, { send: () => unhideCoin(base), update: setState })),
    [act],
  );

  return { state, hide, unhide, pendingBases, reload };
}
